import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { COLORS } from '../utils/constants';
import { fluid } from '../utils/responsive';
import CalendarIcon from '../../../pages/userManagementV2/dashboardV3/calendar.svg';
import ArrowLeft from '../../../pages/userManagementV2/dashboardV3/arrow-single-left.svg';
import ArrowRight from '../../../pages/userManagementV2/dashboardV3/arrow-single-right.svg';

const FILTER_TABS = [
    { key: 'all', label: 'All time', width: '78px' },
    { key: 'last30', label: 'Last 30 days', width: '104px' },
    { key: 'custom', label: 'Custom', width: '78px' },
];

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Builds the /userv2/staff/stats query params for the currently selected
// tab — 'all' needs no date info (server defaults year to the current
// year), 'last30' is a fixed rolling window, 'custom' sends the picked range.
const buildRangeParams = (tab, rangeStart, rangeEnd) => {
    if (tab === 'last30') return { range: 'last30' };
    if (tab === 'custom' && rangeStart) {
        // Plain "YYYY-MM-DD" — not .toISOString(), which converts to UTC and
        // shifts the date backward by the local timezone offset (e.g. IST
        // midnight on the 17th becomes "...T18:30:00Z" on the 16th).
        return {
            range: 'custom',
            startDate: rangeStart.format('YYYY-MM-DD'),
            endDate: (rangeEnd || rangeStart).format('YYYY-MM-DD'),
        };
    }
    return { range: 'all' };
};

const isDateEqual = (d1, d2) => {
    if (!d1 && !d2) return true;
    if (!d1 || !d2) return false;
    return moment(d1).isSame(d2, 'day');
};

const DateRangeFilterPopover = React.memo(({ onApply }) => {
    const [open, setOpen] = useState(false);

    // Committed / applied filter state (what actually takes effect and what trigger button displays)
    const [appliedTab, setAppliedTab] = useState('all');
    const [appliedRangeStart, setAppliedRangeStart] = useState(null);
    const [appliedRangeEnd, setAppliedRangeEnd] = useState(null);
    const [appliedViewMonth, setAppliedViewMonth] = useState(() => moment());

    // Draft / transient state inside the popover (only committed on Apply)
    const [activeTab, setActiveTab] = useState('all');
    const [viewMonth, setViewMonth] = useState(() => moment());
    const [rangeStart, setRangeStart] = useState(null);
    const [rangeEnd, setRangeEnd] = useState(null);

    // Year view inside the custom calendar (opened by clicking the year).
    const [yearView, setYearView] = useState(false);
    const [decadeStart, setDecadeStart] = useState(() => Math.floor(moment().year() / 10) * 10);

    const [position, setPosition] = useState(null);
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);

    const recalculatePosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
            top: rect.bottom + 8,
            right: Math.max(window.innerWidth - rect.right, 16),
        });
    }, []);

    const openPopover = useCallback(() => {
        recalculatePosition();
        setActiveTab(appliedTab);
        setRangeStart(appliedRangeStart);
        setRangeEnd(appliedRangeEnd);
        setViewMonth(appliedViewMonth ? appliedViewMonth.clone() : moment());
        setOpen(true);
    }, [appliedTab, appliedRangeStart, appliedRangeEnd, appliedViewMonth, recalculatePosition]);

    const closePopover = useCallback(() => {
        setActiveTab(appliedTab);
        setRangeStart(appliedRangeStart);
        setRangeEnd(appliedRangeEnd);
        setViewMonth(appliedViewMonth ? appliedViewMonth.clone() : moment());
        setOpen(false);
    }, [appliedTab, appliedRangeStart, appliedRangeEnd, appliedViewMonth]);

    const toggleOpen = useCallback(() => {
        if (!open) {
            openPopover();
        } else {
            closePopover();
        }
    }, [open, openPopover, closePopover]);

    useEffect(() => {
        if (!open) return undefined;

        const handleClickOutside = (e) => {
            if (
                popoverRef.current && !popoverRef.current.contains(e.target)
                && triggerRef.current && !triggerRef.current.contains(e.target)
            ) {
                closePopover();
            }
        };
        const handleReposition = () => recalculatePosition();

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("resize", handleReposition);
        window.addEventListener("scroll", handleReposition, true);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("resize", handleReposition);
            window.removeEventListener("scroll", handleReposition, true);
        };
    }, [open, closePopover, recalculatePosition]);

    // Always come back to the month view when the popover closes or the
    // tab changes.
    useEffect(() => {
        if (!open || activeTab !== 'custom') setYearView(false);
    }, [open, activeTab]);

    const currentYear = moment().year();
    const yearCells = useMemo(
        () => Array.from({ length: 12 }, (_, i) => decadeStart - 1 + i),
        [decadeStart]
    );
    const isNextDecadeDisabled = decadeStart + 10 > currentYear;

    const openYearView = () => {
        setDecadeStart(Math.floor(viewMonth.year() / 10) * 10);
        setYearView(true);
    };

    const handleYearSelect = (year) => {
        if (year > currentYear) return;
        // Don't land on a future month (e.g. Dec of the current year).
        setViewMonth((m) => moment.min(m.clone().year(year), moment().startOf('month')));
        setYearView(false);
    };

    // No future months / days in the custom range.
    const isNextMonthDisabled = !viewMonth.isBefore(moment(), 'month');

    const dateCells = useMemo(() => {
        const startOfMonth = viewMonth.clone().startOf('month');
        const leadingBlanks = startOfMonth.day();
        const daysInMonth = viewMonth.daysInMonth();
        const cells = [];
        for (let i = 0; i < leadingBlanks; i++) cells.push(null);
        for (let day = 1; day <= daysInMonth; day++) cells.push(day);
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, [viewMonth]);

    const cellDates = useMemo(
        () => dateCells.map((day) => (day === null ? null : viewMonth.clone().date(day).startOf('day'))),
        [dateCells, viewMonth]
    );

    const effectiveEnd = rangeEnd || rangeStart;

    const inRangeFlags = useMemo(() => cellDates.map((d) => (
        Boolean(d && rangeStart && effectiveEnd && d.isBetween(rangeStart, effectiveEnd, 'day', '[]'))
    )), [cellDates, rangeStart, effectiveEnd]);

    const handleDayClick = (day) => {
        const clicked = viewMonth.clone().date(day).startOf('day');
        if (clicked.isAfter(moment(), 'day')) return;
        if (!rangeStart || (rangeStart && rangeEnd)) {
            setRangeStart(clicked);
            setRangeEnd(null);
        } else if (clicked.isBefore(rangeStart, 'day')) {
            setRangeEnd(rangeStart);
            setRangeStart(clicked);
        } else {
            setRangeEnd(clicked);
        }
    };

    const triggerLabel = FILTER_TABS.find((tab) => tab.key === appliedTab)?.label || 'All time';

    const hasChanged = useMemo(() => {
        if (activeTab !== appliedTab) return true;
        if (activeTab === 'custom') {
            const startChanged = !isDateEqual(rangeStart, appliedRangeStart);
            const endChanged = !isDateEqual(rangeEnd, appliedRangeEnd);
            return startChanged || endChanged;
        }
        return false;
    }, [activeTab, appliedTab, rangeStart, appliedRangeStart, rangeEnd, appliedRangeEnd]);

    const isDefault = appliedTab === 'all' && activeTab === 'all';
    const isResetDisabled = isDefault;
    const isApplyDisabled = isDefault || (activeTab === 'custom' && !rangeStart);

    const handleReset = () => {
        if (isResetDisabled) return;
        setActiveTab('all');
        setViewMonth(moment());
        setRangeStart(null);
        setRangeEnd(null);
        setAppliedTab('all');
        setAppliedRangeStart(null);
        setAppliedRangeEnd(null);
        setAppliedViewMonth(moment());
        setOpen(false);
        onApply?.(buildRangeParams('all', null, null));
    };

    const handleApply = () => {
        if (isApplyDisabled) return;
        setAppliedTab(activeTab);
        setAppliedRangeStart(rangeStart);
        setAppliedRangeEnd(rangeEnd);
        setAppliedViewMonth(viewMonth.clone());
        setOpen(false);
        onApply?.(buildRangeParams(activeTab, rangeStart, rangeEnd));
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={toggleOpen}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: fluid(8),
                    width: fluid(215),
                    height: fluid(36),
                    padding: `0 ${fluid(12)}`,
                    borderRadius: fluid(8),
                    border: `1px solid ${COLORS.borderDark}`,
                    backgroundColor: "transparent",
                    color: "#1A1A1A",
                    fontSize: fluid(16),
                    fontWeight: 400,
                    flexShrink: 0,
                    boxSizing: "border-box",
                    cursor: "pointer",
                }}
            >
                {triggerLabel}
                <img src={CalendarIcon} alt="" style={{ width: fluid(20), height: fluid(20), flexShrink: 0 }} />
            </button>

            {open && position && ReactDOM.createPortal(
                <div
                    ref={popoverRef}
                    style={{
                        position: "fixed",
                        top: `${position.top}px`,
                        right: `${position.right}px`,
                        width: fluid(473),
                        maxWidth: "calc(100vw - 32px)",
                        backgroundColor: "#FFFFFF",
                        borderRadius: fluid(20),
                        boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                        padding: fluid(24),
                        zIndex: 1000,
                        boxSizing: "border-box",
                    }}
                >
                    <div style={{ display: "flex", gap: fluid(8), marginBottom: fluid(16) }}>
                        {FILTER_TABS.map((tab) => {
                            const isActive = tab.key === activeTab;
                            return (
                                <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => setActiveTab(tab.key)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minWidth: fluid(parseInt(tab.width, 10)),
                                        height: fluid(34),
                                        padding: `${fluid(8)} ${fluid(18)}`,
                                        borderRadius: fluid(8),
                                        border: "none",
                                        backgroundColor: isActive ? COLORS.primary : "#F1F1F1",
                                        color: isActive ? "#FFFFFF" : COLORS.primary,
                                        fontSize: fluid(14),
                                        lineHeight: 1,
                                        fontWeight: 500,
                                        cursor: "pointer",
                                        whiteSpace: "nowrap",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    {activeTab === 'custom' && (
                    <div style={{
                        backgroundColor: COLORS.subtleBg,
                        borderRadius: fluid(24),
                        padding: fluid(20),
                        boxSizing: "border-box",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: fluid(18) }}>
                            {yearView ? (
                                <span style={{ fontSize: fluid(20), fontWeight: 500, color: COLORS.textBlack }}>
                                    {`${decadeStart} - ${decadeStart + 9}`}
                                </span>
                            ) : (
                                <span style={{ fontSize: fluid(20), fontWeight: 500, color: COLORS.textBlack }}>
                                    {viewMonth.format("MMMM")}{" "}
                                    <button
                                        type="button"
                                        onClick={openYearView}
                                        aria-label="Select year"
                                        style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", font: "inherit", color: "inherit" }}
                                    >
                                        {viewMonth.format("YYYY")}
                                    </button>
                                </span>
                            )}
                            <div style={{ display: "flex", alignItems: "center", gap: fluid(44) }}>
                                <button
                                    type="button"
                                    onClick={() => (yearView
                                        ? setDecadeStart((d) => d - 10)
                                        : setViewMonth((m) => m.clone().subtract(1, "month")))}
                                    aria-label={yearView ? "Previous years" : "Previous month"}
                                    style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", display: "flex" }}
                                >
                                    <img src={ArrowLeft} alt="" style={{ width: fluid(24), height: fluid(24) }} />
                                </button>
                                <button
                                    type="button"
                                    disabled={yearView ? isNextDecadeDisabled : isNextMonthDisabled}
                                    onClick={() => (yearView
                                        ? setDecadeStart((d) => d + 10)
                                        : setViewMonth((m) => m.clone().add(1, "month")))}
                                    aria-label={yearView ? "Next years" : "Next month"}
                                    style={{
                                        border: "none",
                                        background: "transparent",
                                        padding: 0,
                                        cursor: (yearView ? isNextDecadeDisabled : isNextMonthDisabled) ? "not-allowed" : "pointer",
                                        opacity: (yearView ? isNextDecadeDisabled : isNextMonthDisabled) ? 0.3 : 1,
                                        display: "flex",
                                    }}
                                >
                                    <img src={ArrowRight} alt="" style={{ width: fluid(24), height: fluid(24) }} />
                                </button>
                            </div>
                        </div>

                        {yearView ? (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: fluid(12), columnGap: fluid(12) }}>
                            {yearCells.map((year) => {
                                const isFuture = year > currentYear;
                                const isOutOfDecade = year < decadeStart || year > decadeStart + 9;
                                const isSelected = year === viewMonth.year();
                                return (
                                    <button
                                        key={year}
                                        type="button"
                                        disabled={isFuture}
                                        onClick={() => handleYearSelect(year)}
                                        style={{
                                            height: fluid(40),
                                            border: "none",
                                            borderRadius: fluid(8),
                                            backgroundColor: isSelected ? COLORS.primary : "transparent",
                                            color: isSelected ? "#FFFFFF" : (isOutOfDecade ? COLORS.axisGray : COLORS.textBlack),
                                            fontSize: fluid(16),
                                            fontWeight: isSelected ? 500 : 400,
                                            cursor: isFuture ? "not-allowed" : "pointer",
                                            opacity: isFuture ? 0.35 : 1,
                                        }}
                                    >
                                        {year}
                                    </button>
                                );
                            })}
                        </div>
                        ) : (
                        <>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: fluid(8) }}>
                            {WEEKDAY_LABELS.map((dayLabel) => (
                                <span key={dayLabel} style={{ fontSize: fluid(14), fontWeight: 400, color: COLORS.axisGray, textAlign: "center" }}>
                                    {dayLabel}
                                </span>
                            ))}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: fluid(11) }}>
                            {dateCells.map((day, index) => {
                                if (day === null) return <span key={`blank-${index}`} />;

                                const col = index % 7;
                                const isInRange = inRangeFlags[index];
                                const prevInRow = col > 0 && inRangeFlags[index - 1];
                                const nextInRow = col < 6 && inRangeFlags[index + 1];
                                const leftRound = isInRange && !prevInRow;
                                const rightRound = isInRange && !nextInRow;

                                const cellDate = cellDates[index];
                                const isStart = Boolean(cellDate && rangeStart && cellDate.isSame(rangeStart, 'day'));
                                const isEnd = Boolean(cellDate && effectiveEnd && cellDate.isSame(effectiveEnd, 'day'));
                                const isToday = Boolean(cellDate && cellDate.isSame(moment(), 'day'));
                                const isFutureDay = Boolean(cellDate && cellDate.isAfter(moment(), 'day'));

                                return (
                                    <button
                                        key={day}
                                        type="button"
                                        disabled={isFutureDay}
                                        onClick={() => handleDayClick(day)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: fluid(32),
                                            border: "none",
                                            padding: 0,
                                            backgroundColor: isInRange ? "#DFDEF5" : "transparent",
                                            borderRadius: `${leftRound ? 100 : 0}px ${rightRound ? 100 : 0}px ${rightRound ? 100 : 0}px ${leftRound ? 100 : 0}px`,
                                            cursor: isFutureDay ? "not-allowed" : "pointer",
                                            opacity: isFutureDay ? 0.35 : 1,
                                        }}
                                    >
                                        <span style={{
                                            width: fluid(32),
                                            height: fluid(32),
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "50%",
                                            boxSizing: "border-box",
                                            border: (isToday && !isStart && !isEnd) ? `2px solid ${COLORS.primary}` : "none",
                                            backgroundColor: (isStart || isEnd) ? COLORS.primary : "transparent",
                                            color: (isStart || isEnd) ? "#FFFFFF" : (isToday ? COLORS.primary : COLORS.textBlack),
                                            fontSize: fluid(16),
                                            fontWeight: isToday ? 700 : 400,
                                            flexShrink: 0,
                                        }}>
                                            {day}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        </>
                        )}
                    </div>
                    )}

                    <div style={{ width: "100%", height: "1px", backgroundColor: COLORS.borderLight, margin: `${fluid(28)} 0` }} />

                    <div style={{ display: "flex", justifyContent: "space-between", gap: fluid(12) }}>
                        <button
                            type="button"
                            disabled={isResetDisabled}
                            onClick={handleReset}
                            style={{
                                width: fluid(113),
                                height: fluid(45),
                                borderRadius: fluid(12),
                                border: `1.5px solid ${COLORS.primary}`,
                                backgroundColor: "transparent",
                                color: COLORS.primary,
                                fontSize: fluid(16),
                                fontWeight: 700,
                                cursor: isResetDisabled ? "not-allowed" : "pointer",
                                opacity: isResetDisabled ? 0.5 : 1,
                                transition: "opacity 0.2s ease",
                            }}
                        >
                            RESET
                        </button>
                        <button
                            type="button"
                            disabled={isApplyDisabled}
                            onClick={handleApply}
                            style={{
                                width: fluid(112),
                                height: fluid(45),
                                borderRadius: fluid(12),
                                border: "none",
                                backgroundColor: COLORS.primary,
                                color: "#FFFFFF",
                                fontSize: fluid(16),
                                fontWeight: 700,
                                cursor: isApplyDisabled ? "not-allowed" : "pointer",
                                opacity: isApplyDisabled ? 0.5 : 1,
                                transition: "opacity 0.2s ease",
                            }}
                        >
                            APPLY
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
});

DateRangeFilterPopover.displayName = 'DateRangeFilterPopover';

export default DateRangeFilterPopover;
